<template>
    <div class="enps-direction-chart">
        <div class="enps-direction-chart__header">
            <div class="enps-direction-chart__title">Средняя оценка по категориям обращений</div>
        </div>

        <div class="enps-chart-placeholder" v-if="!hasChartData" :style="{ height: chartHeight }">
            Нет оценок за период
        </div>
        <Bar
            v-else
            :chart-data="chartData"
            :chart-options="chartOptions"
            :styles="{ height: chartHeight, position: 'relative' }"
        ></Bar>

        <button
            v-if="categoryChart.rest && !isExpanded"
            type="button"
            class="enps-direction-chart__expand"
            @click="isExpanded = true"
        >
            Показать остальные {{ categoryChart.rest.count }}
        </button>
        <button
            v-else-if="categoryChart.rest && isExpanded"
            type="button"
            class="enps-direction-chart__expand"
            @click="isExpanded = false"
        >
            Свернуть
        </button>
    </div>
</template>
<script>
import { Bar } from 'vue-chartjs/legacy';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { SERIES, ratingScaleColor } from '@/js/enpsColors';

const REST_COLOR = SERIES.rest;

// pt.1 (откат уровня направлений): плоский список категорий обращений (request_categories),
// без промежуточного уровня направлений и без бакета "Без направления" — только реально
// оценённые за период категории (без zero-fill, поэтому пустых 0-оценочных строк не бывает).
// pt.3/pt.4 масштабирования сохранены: горизонтальный бар-чарт (indexAxis:'y'), топ-N по числу
// оценок с "Остальные N" сверху, приглушённые бары для категорий с числом оценок ниже порога
// (categoryChart.top[].isLowSample с бэкенда). Структурно — точная копия EnpsDepartmentChart.vue.
@Component({
    components: {
        Bar,
    },
})
class EnpsCategoryChart extends Vue {
    @Prop({ type: Object, default: () => ({ top: [], rest: null }) })
    categoryChart;
    @Prop({ type: String, required: true })
    period;

    isExpanded = false;

    get items() {
        if (this.isExpanded && this.categoryChart.rest) {
            return [...this.categoryChart.top, ...this.categoryChart.rest.items];
        }

        return this.categoryChart.top;
    }

    get hasChartData() {
        return this.categoryChart.top.length > 0;
    }

    get chartHeight() {
        const itemCount = this.items.length + (this.categoryChart.rest && !this.isExpanded ? 1 : 0);

        return `${Math.max(220, itemCount * 34 + 40)}px`;
    }

    get chartData() {
        const labels = this.items.map((row) => [row.categoryTitle, `(${row.ratingsCount} оценок)`]);
        const data = this.items.map((row) => row.averageRating);
        // Цвет столбца — строго по значению (единая шкала >4 / 3-4 / <3 из enpsColors.js,
        // одна на все столбчатые графики): цветов ровно три, малая выборка цветом не кодируется
        // — число оценок и так напечатано в подписи строки.
        const colors = this.items.map((row) => ratingScaleColor(row.averageRating));

        if (this.categoryChart.rest && !this.isExpanded) {
            const rest = this.categoryChart.rest;
            labels.push(['Остальные', `(${rest.count} категорий, ${rest.ratingsCount} оценок)`]);
            data.push(rest.averageRating);
            colors.push(REST_COLOR);
        }

        return {
            labels,
            datasets: [
                {
                    label: 'Средняя оценка',
                    data,
                    backgroundColor: colors,
                },
            ],
        };
    }

    get chartOptions() {
        return {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
            },
            scales: {
                x: { min: 0, max: 5 },
            },
        };
    }

    @Watch('period')
    onPeriodChange() {
        this.isExpanded = false;
    }
}

export default EnpsCategoryChart;
</script>
<style lang="scss">
.enps-direction-chart {
    flex: 1;
    padding: 15px;
    border-radius: 8px;
    background-color: var(--color-white);
    box-shadow: 0px 1px 10px rgba(88, 88, 88, 0.15);

    &__header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
    }

    &__title {
        font-weight: 600;
    }

    &__expand {
        display: block;
        margin-top: 10px;
        padding: 0;
        border: none;
        background: none;
        color: #c05a1f; // ACCENT_DEEP из enpsColors.js — интерфейсная ссылка, не данные
        cursor: pointer;
        font-size: 0.875rem;

        &:hover {
            text-decoration: underline;
        }
    }
}

.enps-chart-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a0a0a0;
}
</style>
